import React, { useState, useEffect } from 'react';
import { X, Save, Settings, Key, User, DollarSign, BarChart3 } from 'lucide-react';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';
import { PartnerApiKeysModal } from './PartnerApiKeysModal.tsx';
import { fetchMarketCategories, Category } from '../services/categoryService.ts';

interface Partner {
  id: string;
  email: string;
  role: string;
  created_at: string;
  last_sign_in_at: string | null;
  is_blocked: boolean;
  fica_verified: boolean;
  phone: string | null;
  is_sso_user: boolean;
  is_anonymous: boolean;
  wallet_balance: number | null;
  email_confirmed_at: string | null;
  partner_name?: string;
  commission_rate?: number;
  allowed_categories?: string[];
  max_markets_per_day?: number;
  markets_created_today?: number;
  total_markets_created?: number;
  last_api_call?: string | null;
}

interface PartnerManagementModalProps {
  partner: Partner;
  onClose: () => void;
  onUpdate: () => void;
}

export function PartnerManagementModal({ partner, onClose, onUpdate }: PartnerManagementModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showApiKeysModal, setShowApiKeysModal] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  
  // Form state
  const [partnerName, setPartnerName] = useState('');
  const [commissionRate, setCommissionRate] = useState(1);
  const [allowedCategories, setAllowedCategories] = useState<string[]>([]);
  const [maxMarketsPerDay, setMaxMarketsPerDay] = useState(50);

  // Fetch categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await fetchMarketCategories();
        setCategories(categoriesData);
      } catch (err) {
        logger.error('Failed to load categories:', err);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    if (partner) {
      setPartnerName(partner.partner_name || partner.email);
      setCommissionRate((partner.commission_rate || 0.01) * 100);
      setAllowedCategories(partner.allowed_categories || []);
      setMaxMarketsPerDay(partner.max_markets_per_day || 50);
    }
  }, [partner]);

  const handleCategoryToggle = (cat: string) => {
    setAllowedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partner) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Update partner metadata
      const { error: metaError } = await supabase.rpc('admin_update_partner_metadata', {
        p_user_id: partner.id,
        p_partner_name: partnerName,
        p_commission_rate: commissionRate / 100,
        p_allowed_categories: allowedCategories,
        p_max_markets_per_day: maxMarketsPerDay
      });

      if (metaError) throw metaError;

      setSuccess('Partner updated successfully');
      onUpdate();
      
      // Clear success message after 2 seconds
      setTimeout(() => {
        setSuccess(null);
        onClose();
      }, 2000);

    } catch (err) {
      logger.error('Error updating partner:', err);
      setError(err instanceof Error ? err.message : 'Failed to update partner');
    } finally {
      setLoading(false);
    }
  };

  if (!partner) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Settings className="w-5 h-5 text-wayja-teal" />
              Manage Partner: {partner.partner_name || partner.email}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Partner Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium">Markets Created</span>
              </div>
              <div className="text-2xl font-bold">{partner.total_markets_created || 0}</div>
              <div className="text-xs text-gray-500">Today: {partner.markets_created_today || 0}</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">Commission Rate</span>
              </div>
              <div className="text-2xl font-bold">{((partner.commission_rate || 0.01) * 100).toFixed(1)}%</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-medium">Status</span>
              </div>
              <div className="text-2xl font-bold">{partner.is_blocked ? 'Blocked' : 'Active'}</div>
              <div className="text-xs text-gray-500">
                Last API Call: {partner.last_api_call ? new Date(partner.last_api_call).toLocaleDateString() : 'Never'}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setShowApiKeysModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              <Key className="w-4 h-4" />
              Manage API Keys
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          <div className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Partner Name</label>
                <input
                  type="text"
                  value={partnerName}
                  onChange={e => setPartnerName(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Commission Rate (%)</label>
                <input
                  type="number"
                  min={0.1}
                  max={50}
                  step={0.1}
                  value={commissionRate}
                  onChange={e => setCommissionRate(Number(e.target.value))}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Allowed Categories</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => (
                    <button
                      type="button"
                      key={cat.id}
                      className={`px-2 py-1 rounded border text-xs ${allowedCategories.includes(cat.name) ? 'bg-wayja-teal text-white border-wayja-teal' : 'bg-white text-gray-700 border-gray-300'}`}
                      onClick={() => handleCategoryToggle(cat.name)}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Max Markets Per Day</label>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={maxMarketsPerDay}
                  onChange={e => setMaxMarketsPerDay(Number(e.target.value))}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-wayja-teal text-white rounded hover:bg-wayja-teal/90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showApiKeysModal && (
        <PartnerApiKeysModal
          partnerName={partner.partner_name || partner.email}
          partnerId={partner.id}
          onClose={() => setShowApiKeysModal(false)}
        />
      )}
    </>
  );
} 